import * as Dialog from "@radix-ui/react-dialog";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowRight, Lock, X } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import type { PaywallPayload } from "@/common/services/billing";
import { Button } from "@/components/ui/button";

/**
 * Global paywall modal. Listens for the `paywall:open` window event dispatched
 * by the axios response interceptor when the backend returns HTTP 402.
 *
 * Dismissable; will re-open on every subsequent 402 (no rate-limiting).
 */
// Module-level cooldown — ignore bursts of paywall events within 2 s of each other
let lastPaywallEventMs = 0;
const PAYWALL_COOLDOWN_MS = 2000;

const PaywallModal = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [payload, setPayload] = useState<PaywallPayload | null>(null);
  const isOpenRef = useRef(false);

  useEffect(() => {
    const handler = (event: Event) => {
      const now = Date.now();
      // Ignore if modal is already open, or if a paywall event fired very recently
      if (isOpenRef.current) return;
      if (now - lastPaywallEventMs < PAYWALL_COOLDOWN_MS) return;
      lastPaywallEventMs = now;
      const detail = (event as CustomEvent<PaywallPayload | undefined>).detail;
      setPayload(detail ?? null);
      setOpen(true);
      isOpenRef.current = true;
      // The 402 means a counter just rolled over server-side; refresh the
      // usage snapshot so the sidebar + Billing page reflect reality.
      queryClient.invalidateQueries({ queryKey: ["billing", "usage"] });
    };
    window.addEventListener("paywall:open", handler);
    return () => window.removeEventListener("paywall:open", handler);
  }, [queryClient]);

  const feature = payload?.feature;
  const reason = payload?.reason;
  const upgradeUrl = payload?.upgrade_url || "/billing";
  const isDailyQuota = reason === "daily_quota_exceeded";

  const dailyHeadline =
    feature === "ai"
      ? "Daily AI generation limit reached"
      : feature === "scheduled_runs"
        ? "Daily scheduled run limit reached"
        : feature === "automated_runs"
          ? "Daily automated run limit reached"
          : "Daily limit reached";

  const headline = isDailyQuota
    ? dailyHeadline
    : feature === "ai"
      ? "Unlock AI features"
      : feature === "test_cases"
        ? "You've reached your test case limit"
        : "Upgrade required";

  const dailyBody = `You've used ${payload?.used ?? "all"} of ${payload?.limit ?? "your"} included ${
    feature === "ai"
      ? "AI generations"
      : feature === "scheduled_runs"
        ? "scheduled runs"
        : feature === "automated_runs"
          ? "automated runs"
          : "actions"
  } today. Your quota resets at midnight UTC, or upgrade for a higher cap.`;

  const body = isDailyQuota
    ? dailyBody
    : feature === "ai"
      ? "AI test generation, defect analysis and analytics insights are available on the Starter plan and above. Upgrade to start automating with AI."
      : feature === "test_cases"
        ? `You're using ${payload?.used ?? "all"} of ${payload?.limit ?? "your"} included test cases on your current plan. Upgrade to add more.`
        : "Your current plan doesn't include this feature. Upgrade to continue.";

  const handleSeePlans = useCallback(() => {
    setOpen(false);
    isOpenRef.current = false;
    navigate(upgradeUrl);
  }, [navigate, upgradeUrl]);

  const handleDismiss = useCallback(() => {
    setOpen(false);
    isOpenRef.current = false;
  }, []);

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(v) => {
        setOpen(v);
        if (!v) isOpenRef.current = false;
      }}
    >
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[80] bg-black/40 backdrop-blur-sm data-[state=open]:animate-overlay-in data-[state=closed]:animate-overlay-out" />
        <Dialog.Content
          className="bg-white fixed top-[50%] left-[50%] translate-x-[-50%] translate-y-[-50%] w-[90vw] max-w-[440px] z-[90] rounded-2xl shadow-2xl ring-1 ring-black/5 overflow-hidden data-[state=open]:animate-dialog-in data-[state=closed]:animate-dialog-out"
          aria-describedby="paywall-modal-description"
        >
          <div className="px-6 pt-6 pb-2">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
                  <Lock size={18} aria-hidden="true" />
                </span>
                <Dialog.Title className="text-lg font-semibold text-foreground">
                  {headline}
                </Dialog.Title>
              </div>
              <button
                type="button"
                onClick={handleDismiss}
                aria-label="Dismiss upgrade prompt"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md text-gray-500 hover:text-gray-900 hover:bg-gray-100 transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <X size={16} aria-hidden="true" />
              </button>
            </div>
            <Dialog.Description
              id="paywall-modal-description"
              className="mt-3 text-sm text-muted-foreground leading-relaxed"
            >
              {body}
            </Dialog.Description>
          </div>

          <div className="px-6 py-5 mt-2 bg-gray-50 border-t border-gray-100 flex items-center justify-end gap-2">
            <Button
              type="button"
              variant="ghost"
              onClick={handleDismiss}
              className="cursor-pointer min-h-[44px]"
            >
              Maybe later
            </Button>
            <Button
              type="button"
              onClick={handleSeePlans}
              className="cursor-pointer min-h-[44px]"
            >
              See plans
              <ArrowRight size={14} aria-hidden="true" />
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default PaywallModal;
